import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleOutlined,
  ClearOutlined,
  CloudOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  LockOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  SearchOutlined,
  StopOutlined,
  SyncOutlined,
  TeamOutlined,
  UnlockOutlined,
  UserAddOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { KeyRound } from 'lucide-react';

import { Bento, DataTable, MenuThaoTac, PageHeader, StatTile, Tile } from '../../../components/shared';
import { Button, Empty, Input, Select, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { KHONG_CO, soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { KhongCoQuyen, LoiRpa } from '../components/chung';
import { HopGiaHan } from '../components/license/HopGiaHan';
import { HopLicense } from '../components/license/HopLicense';
import { HopQuyenOpenCore } from '../components/license/HopQuyenOpenCore';
import { NganLicense } from '../components/license/NganLicense';
import { BadgeLoaiLicense, BadgeTrangThaiLicense, BadgeTruyCap, HanLicense, OThietBi } from '../components/license/nhan';
import { QUYEN, TRANG_THAI_LICENSE_HIEN } from '../constants';
import { useUsersOpenCore } from '../hooks/useDanhBaOpenCore';
import { useDoiTrangThaiLicense, useDongBoLicense, useLicenses, useResetThietBi, useXoaLicense } from '../hooks/useLicense';
import { useProjects } from '../hooks/useOpenCore';
import { KHOA_RPA } from '../hooks/useRpa';
import {
  demSapHetHan,
  dungDong,
  LOC_THIET_BI,
  locDong,
  loaiLicense,
  NGAY_SAP_HET_HAN,
  tenProjectLicense,
  thietBiDangBind,
  TRANG_THAI_USER_LICENSE,
  workflowCuaLicense,
  type BoLocLicense,
  type DongUserLicense,
} from '../nghiep-vu/license';
import type { LicenseRpa } from '../types';

type Hop =
  | { kieu: 'cap'; userId: string }
  | { kieu: 'sua'; license: LicenseRpa }
  | { kieu: 'giaHan'; license: LicenseRpa }
  | { kieu: 'quyen'; license: LicenseRpa }
  | { kieu: 'khoa' | 'thuHoi' | 'reset' | 'xoa'; license: LicenseRpa }
  | null;

const LOC_GOC: BoLocLicense = { tim: '', status: '', thietBi: '' };

/**
 * Quản lý License RPA (`/rpa/licenses`, `RpaLicensesView` của native): mỗi dòng một License (gắn User OpenCore), cộng
 * một dòng cho mỗi User OpenCore chưa có License để cấp nhanh. Native chặn cả màn theo cờ quản trị; ở đây theo bốn quyền
 * `rpa:license:{read,create,update,delete}`, thiếu quyền thì ẩn thao tác. Lọc ở máy khách như native.
 */
export function LicenseView() {
  const { t } = useT();
  const qc = useQueryClient();
  const quyen = useModulePermissions(QUYEN);
  const ds = useLicenses(quyen.licenseDoc);
  const users = useUsersOpenCore(quyen.licenseDoc);
  const tieuDe = t('nav.muc.quanLyLicenseRpa');

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[{ label: t('rpa.nhom') }, { label: tieuDe }]}
        titleIcon={<KeyRound size={18} className="text-primary-600" aria-hidden />}
        title={tieuDe}
        subtitle={t('rpaLicense.moTa')}
        actions={
          quyen.licenseDoc ? (
            <Button
              variant="outline"
              size="md"
              icon={<ReloadOutlined />}
              loading={(ds.isFetching && !ds.isPending) || (users.isFetching && !users.isPending)}
              onClick={() => {
                void qc.invalidateQueries({ queryKey: KHOA_RPA.licenses });
                void qc.invalidateQueries({ queryKey: KHOA_RPA.usersOpenCore });
              }}
            >
              {t('chung.lamMoi')}
            </Button>
          ) : undefined
        }
      />
      {!quyen.licenseDoc ? (
        <KhongCoQuyen quyen={QUYEN.licenseDoc} />
      ) : ds.isPending ? (
        <div className="space-y-4" aria-busy="true">
          <Bento>
            {[0, 1, 2, 3].map((i) => (
              <Tile key={i} span="sm:col-span-6 lg:col-span-3">
                <Skeleton className="h-20 w-full" />
              </Tile>
            ))}
          </Bento>
          <Tile>
            <Skeleton className="h-64 w-full" />
          </Tile>
        </div>
      ) : ds.isError ? (
        <Tile>
          <LoiRpa loi={ds.error} onThuLai={() => void ds.refetch()} />
        </Tile>
      ) : (
        <NoiDung licenses={ds.data.data} quyen={quyen} />
      )}
    </div>
  );
}

function NoiDung({ licenses, quyen }: { licenses: LicenseRpa[]; quyen: Record<keyof typeof QUYEN, boolean> }) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const users = useUsersOpenCore(true);
  // Project OpenRPA cho biểu mẫu và Workflow của License cũ; thiếu `rpa:project:read` thì không gọi (máy chủ trả 403).
  const projects = useProjects(quyen.projectDoc);
  const dongBo = useDongBoLicense();
  const [loc, setLoc] = useState<BoLocLicense>(LOC_GOC);
  const [hop, setHop] = useState<Hop>(null);
  const [dangXem, setDangXem] = useState<string | null>(null);

  const dsUser = users.data?.data ?? [];
  const dong = useMemo(() => dungDong(licenses, dsUser), [licenses, dsUser]);
  const loc1 = locDong(dong, loc);
  const dangLoc = loc.tim.trim() !== '' || loc.status !== '' || loc.thietBi !== '';
  const loiUsers = users.isError ? `${t('rpaLicense.khongTaiUser')} ${dichLoi(users.error)}` : null;
  const workflowCua = (l: LicenseRpa) => workflowCuaLicense(l, projects.data ?? []);
  const xem = dangXem ? dong.find((r) => r.license?.id === dangXem) : undefined;
  const dat = (thay: Partial<BoLocLicense>) => setLoc((c) => ({ ...c, ...thay }));

  const chayDongBo = (l: LicenseRpa) => {
    if (!l.opencore_user_id) {
      setHop({ kieu: 'sua', license: l });
      toast.warning(t('rpaLicense.dongBoCanUser'));
      return;
    }
    dongBo.mutate(l.id, {
      onSuccess: (kq) => toast.success(t(kq.roleMember ? 'rpaLicense.daDongBo' : 'rpaLicense.daDongBoKhongThanhVien')),
      onError: (e) => toast.error(dichLoi(e)),
    });
  };

  const thaoTac = (r: DongUserLicense) => {
    const l = r.license;
    if (!l) {
      return quyen.licenseTao ? (
        <Button variant="outline" size="sm" icon={<UserAddOutlined />} onClick={() => setHop({ kieu: 'cap', userId: r.user?.id ?? '' })}>
          {t('rpaLicense.cap')}
        </Button>
      ) : null;
    }
    const biKhoa = r.status === 'blocked';
    const thuHoi = r.status === 'revoked';
    const may = thietBiDangBind(l);
    return (
      <MenuThaoTac
        tenBanGhi={`${l.opencore_username || KHONG_CO} · ${tenProjectLicense(l)}`}
        muc={[
          { khoa: 'xem', nhan: t('rpaLicense.xemLicense'), icon: <EyeOutlined />, nhom: 'xem', onChon: () => setDangXem(l.id) },
          { khoa: 'quyen', nhan: t('rpaLicense.kiemQuyen'), icon: <SafetyOutlined />, nhom: 'xem', onChon: () => setHop({ kieu: 'quyen', license: l }) },
          { khoa: 'giaHan', nhan: t('rpaLicense.giaHanNut'), icon: <FieldTimeOutlined />, nhom: 'sua', hien: quyen.licenseSua, onChon: () => setHop({ kieu: 'giaHan', license: l }) },
          { khoa: 'sua', nhan: t('rpaLicense.sua'), icon: <EditOutlined />, nhom: 'sua', hien: quyen.licenseSua, onChon: () => setHop({ kieu: 'sua', license: l }) },
          { khoa: 'dongBo', nhan: t('rpaLicense.dongBo'), icon: <SyncOutlined />, nhom: 'sua', hien: quyen.licenseSua, onChon: () => chayDongBo(l) },
          {
            khoa: 'khoa',
            nhan: biKhoa ? t('rpaLicense.moKhoa') : t('rpaLicense.khoa'),
            icon: biKhoa ? <UnlockOutlined /> : <LockOutlined />,
            nhom: 'sua',
            hien: quyen.licenseSua,
            voHieu: thuHoi,
            ...(thuHoi ? { moTa: t('rpaLicense.thuHoiKhongKhoa') } : {}),
            onChon: () => setHop({ kieu: 'khoa', license: l }),
          },
          {
            khoa: 'reset',
            nhan: t('rpaLicense.resetThietBi'),
            icon: <ClearOutlined />,
            nhom: 'sua',
            hien: quyen.licenseSua,
            voHieu: !may,
            ...(!may ? { moTa: t('rpaLicense.chuaBind') } : {}),
            onChon: () => setHop({ kieu: 'reset', license: l }),
          },
          { khoa: 'thuHoi', nhan: t('rpaLicense.thuHoi'), icon: <StopOutlined />, nhom: 'pha-huy', hien: quyen.licenseSua && !thuHoi, onChon: () => setHop({ kieu: 'thuHoi', license: l }) },
          { khoa: 'xoa', nhan: t('rpaLicense.xoa'), icon: <DeleteOutlined />, nhom: 'pha-huy', hien: quyen.licenseXoa, onChon: () => setHop({ kieu: 'xoa', license: l }) },
        ]}
      />
    );
  };

  return (
    <>
      <Bento>
        <StatTile nhan={t('rpaLicense.kpiTong')} so={soVN(dong.filter((r) => r.license?.opencore_user_id).length, locale)} icon={<TeamOutlined />} tone="primary" />
        <StatTile nhan={t('rpaLicense.kpiActive')} so={soVN(dong.filter((r) => r.status === 'active').length, locale)} icon={<CheckCircleOutlined />} tone="success" />
        <StatTile
          nhan={t('rpaLicense.kpiChuaCap')}
          so={soVN(dong.filter((r) => r.status === 'none' || r.status === 'pending').length, locale)}
          icon={<SafetyCertificateOutlined />}
          tone="warning"
        />
        <StatTile
          nhan={t('rpaLicense.kpiSapHet')}
          so={soVN(demSapHetHan(dong), locale)}
          sub={t('rpaLicense.kpiSapHetSub', { so: soVN(NGAY_SAP_HET_HAN, locale) })}
          icon={<WarningOutlined />}
          tone="danger"
        />
      </Bento>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Input
            allowClear
            autoComplete="off"
            prefix={<SearchOutlined aria-hidden />}
            placeholder={t('rpaLicense.tim')}
            aria-label={t('rpaLicense.tim')}
            value={loc.tim}
            onChange={(e) => dat({ tim: e.target.value })}
            className="w-full sm:w-72"
          />
          <Select<string>
            value={loc.status}
            onChange={(status) => dat({ status })}
            aria-label={t('rpaLicense.locTrangThai')}
            className="w-52"
            options={[
              { value: '', label: t('rpaLicense.moiTrangThai') },
              ...TRANG_THAI_USER_LICENSE.map((s) => ({ value: s, label: t(TRANG_THAI_LICENSE_HIEN[s].khoa) })),
            ]}
          />
          <Select<string>
            value={loc.thietBi}
            onChange={(thietBi) => dat({ thietBi })}
            aria-label={t('rpaLicense.locThietBi')}
            className="w-48"
            options={[
              { value: '', label: t('rpaLicense.moiThietBi') },
              ...LOC_THIET_BI.map((d) => ({ value: d, label: t(d === 'bound' ? 'rpaLicense.daBind' : 'rpaLicense.chuaBindLoc') })),
            ]}
          />
          {dangLoc && (
            <Button variant="ghost" size="sm" onClick={() => setLoc(LOC_GOC)}>
              {t('chung.xoaBoLoc')}
            </Button>
          )}
        </div>
        {quyen.licenseTao && (
          <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ kieu: 'cap', userId: '' })}>
            {t('rpaLicense.cap')}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-content-muted">
        <span role="status">{t('rpaLicense.daTai', { so: soVN(dong.length, locale) })}</span>
        {loiUsers && <span className="text-warning-700">{loiUsers}</span>}
        <span className="inline-flex items-center gap-1">
          <CloudOutlined aria-hidden /> {t('rpaLicense.nguon')}
        </span>
      </div>

      <Tile className="min-w-0">
        {dong.length === 0 ? (
          <Empty
            icon={TeamOutlined}
            title={t('rpaLicense.rong')}
            description={t('rpaLicense.rongMoTa')}
            action={
              quyen.licenseTao ? (
                <Button variant="primary" size="sm" icon={<PlusOutlined />} onClick={() => setHop({ kieu: 'cap', userId: '' })}>
                  {t('rpaLicense.cap')}
                </Button>
              ) : undefined
            }
          />
        ) : loc1.length === 0 ? (
          <Empty icon={SearchOutlined} title={t('rpaLicense.rongLoc')} />
        ) : (
          <DataTable<DongUserLicense>
            caption={t('nav.muc.quanLyLicenseRpa')}
            dataSource={loc1}
            rowKey="khoa"
            pagination={loc1.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
            columns={[
              { title: t('chung.stt'), key: 'stt', width: 56, render: (_, _r, i) => <span className="tabular-nums">{soVN(i + 1, locale)}</span> },
              {
                title: t('rpaLicense.cotUser'),
                key: 'user',
                render: (_, r) => (
                  <div className="min-w-0 max-w-[14rem] leading-tight">
                    <p className="truncate font-medium text-content-primary">
                      {r.license?.opencore_username || r.user?.username || t('rpaLicense.chuaGanUser')}
                    </p>
                    <p className="truncate text-xs text-content-muted">{r.user?.email || r.license?.opencore_email}</p>
                  </div>
                ),
              },
              {
                title: t('rpaLicense.cotProject'),
                key: 'project',
                render: (_, r) =>
                  r.license ? (
                    <div className="min-w-0 max-w-[14rem] leading-tight">
                      <p className="truncate">{tenProjectLicense(r.license)}</p>
                      {r.license.customer_name && <p className="truncate text-xs text-content-muted">{r.license.customer_name}</p>}
                    </div>
                  ) : (
                    <span className="text-content-muted">{KHONG_CO}</span>
                  ),
              },
              {
                title: t('rpaLicense.cotWorkflow'),
                key: 'workflow',
                render: (_, r) => {
                  if (!r.license) return <span className="text-content-muted">{KHONG_CO}</span>;
                  const wf = workflowCua(r.license);
                  if (!wf.length) return <span className="text-content-muted">{KHONG_CO}</span>;
                  return (
                    <span className="block max-w-[12rem] truncate" title={wf.map((w) => w.name).join('\n')}>
                      {wf.length === 1 ? wf[0]!.name : t('rpaLicense.soWorkflow', { so: soVN(wf.length, locale) })}
                    </span>
                  );
                },
              },
              {
                title: t('rpaLicense.cotLicense'),
                key: 'license',
                render: (_, r) => (
                  <div className="flex flex-wrap items-center gap-1">
                    <BadgeTrangThaiLicense status={r.status} />
                    {r.license && <BadgeLoaiLicense loai={loaiLicense(r.license)} />}
                  </div>
                ),
              },
              { title: t('rpaLicense.cotThietBi'), key: 'thietBi', render: (_, r) => <OThietBi license={r.license} /> },
              {
                title: t('rpaLicense.cotHetHan'),
                key: 'han',
                render: (_, r) => (r.license ? <HanLicense license={r.license} /> : <span className="text-content-muted">{KHONG_CO}</span>),
              },
              { title: t('rpaLicense.cotTruyCap'), key: 'truyCap', render: (_, r) => <BadgeTruyCap dong={r} /> },
              {
                title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                key: 'thaoTac',
                width: 120,
                align: 'center',
                fixed: 'right',
                render: (_, r) => thaoTac(r),
              },
            ]}
          />
        )}
      </Tile>

      {(hop?.kieu === 'cap' || hop?.kieu === 'sua') && (
        <HopLicense
          goc={hop.kieu === 'sua' ? hop.license : null}
          userSan={hop.kieu === 'cap' ? hop.userId : ''}
          licenses={licenses}
          users={dsUser}
          loiUsers={loiUsers}
          projects={projects.data ?? []}
          loiProjects={projects.error}
          dangTaiProjects={quyen.projectDoc && projects.isPending}
          thieuQuyenProject={!quyen.projectDoc}
          onXem={(l) => {
            setHop(null);
            setDangXem(l.id);
          }}
          onClose={() => setHop(null)}
        />
      )}
      {hop?.kieu === 'giaHan' && <HopGiaHan license={hop.license} onClose={() => setHop(null)} />}
      {hop?.kieu === 'quyen' && <HopQuyenOpenCore license={hop.license} onClose={() => setHop(null)} />}
      {(hop?.kieu === 'khoa' || hop?.kieu === 'thuHoi' || hop?.kieu === 'reset' || hop?.kieu === 'xoa') && (
        <HopXacNhanLicense kieu={hop.kieu} license={hop.license} onClose={() => setHop(null)} />
      )}
      {xem?.license && (
        <NganLicense
          license={xem.license}
          user={xem.user}
          workflows={workflowCua(xem.license)}
          suaDuoc={quyen.licenseSua}
          onClose={() => setDangXem(null)}
          onGiaHan={() => setHop({ kieu: 'giaHan', license: xem.license! })}
          onKhoa={() => setHop({ kieu: 'khoa', license: xem.license! })}
          onReset={() => setHop({ kieu: 'reset', license: xem.license! })}
        />
      )}
    </>
  );
}

/** Khoá / mở khoá, thu hồi, reset thiết bị, xoá License — tiêu đề hỏi, thân nói hậu quả, nút nhắc lại việc. */
function HopXacNhanLicense({ kieu, license: l, onClose }: { kieu: 'khoa' | 'thuHoi' | 'reset' | 'xoa'; license: LicenseRpa; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const doi = useDoiTrangThaiLicense();
  const reset = useResetThietBi();
  const xoa = useXoaLicense();
  const ten = { user: l.opencore_username || KHONG_CO, project: tenProjectLicense(l), may: thietBiDangBind(l)?.machineName || KHONG_CO };
  const moKhoa = kieu === 'khoa' && l.status === 'locked';
  const dang = kieu === 'reset' ? reset : kieu === 'xoa' ? xoa : doi;

  type Noi = { tieuDe: KhoaDich; than: KhoaDich; nut: KhoaDich; nguy: boolean };
  const bang: Record<typeof kieu, Noi> = {
    khoa: moKhoa
      ? { tieuDe: 'rpaLicense.xacNhan.moKhoaTieuDe', than: 'rpaLicense.xacNhan.moKhoaThan', nut: 'rpaLicense.moKhoa', nguy: false }
      : { tieuDe: 'rpaLicense.xacNhan.khoaTieuDe', than: 'rpaLicense.xacNhan.khoaThan', nut: 'rpaLicense.khoa', nguy: true },
    thuHoi: { tieuDe: 'rpaLicense.xacNhan.thuHoiTieuDe', than: 'rpaLicense.xacNhan.thuHoiThan', nut: 'rpaLicense.thuHoi', nguy: true },
    reset: { tieuDe: 'rpaLicense.xacNhan.resetTieuDe', than: 'rpaLicense.xacNhan.resetThan', nut: 'rpaLicense.resetThietBi', nguy: true },
    xoa: { tieuDe: 'rpaLicense.xacNhan.xoaTieuDe', than: 'rpaLicense.xacNhan.xoaThan', nut: 'rpaLicense.xoa', nguy: true },
  };
  const noi = bang[kieu];

  const xacNhan = () => {
    const xong = (khoa: KhoaDich) => () => {
      toast.success(t(khoa));
      onClose();
    };
    if (kieu === 'reset') reset.mutate(l.id, { onSuccess: xong('rpaLicense.xacNhan.daReset') });
    else if (kieu === 'xoa') xoa.mutate(l.id, { onSuccess: xong('rpaLicense.xacNhan.daXoa') });
    else if (kieu === 'thuHoi') doi.mutate({ id: l.id, status: 'revoked' }, { onSuccess: xong('rpaLicense.xacNhan.daThuHoi') });
    else
      doi.mutate(
        { id: l.id, status: moKhoa ? 'active' : 'locked' },
        {
          onSuccess: (moi) => {
            toast.success(t(moi.access_sync_status === 'error' ? 'rpaLicense.xacNhan.doiHongDongBo' : moKhoa ? 'rpaLicense.xacNhan.daMoKhoa' : 'rpaLicense.xacNhan.daKhoa'));
            onClose();
          },
        },
      );
  };

  return (
    <HopXacNhanThaoTac
      open
      nguyHiem={noi.nguy}
      tieuDe={t(noi.tieuDe, ten)}
      than={t(noi.than, ten)}
      nhanXacNhan={t(noi.nut)}
      dangChay={dang.isPending}
      loi={dang.error ? dichLoi(dang.error) : null}
      onClose={onClose}
      onXacNhan={xacNhan}
    />
  );
}
